import * as swissknife from 'up.lib.js.swissknife';
import { BlsErrorCode } from '~src/domains/common/enums/error';
import { RegionsCA, RegionsUS } from '~src/domains/common/enums/localization';
import { InputStateType } from '~src/domains/common/types/forms';
import * as constants from '~src/utils/constants';
import { isValidDate, postProcessFunctions } from './helpers';
import { secureForms } from './secureForms';

const blsErrorCodeToMessagesEn = {
  [BlsErrorCode.BLS_0]: 'Internal error',
  [BlsErrorCode.BLS_2]: 'This form of payment is not accepted',
  [BlsErrorCode.BLS_3]: 'American Express cards not accepted',
  [BlsErrorCode.BLS_4]: 'Card is not accepted',
  [BlsErrorCode.BLS_5]: 'Visa cards are not accepted',
  [BlsErrorCode.BLS_6]: 'This form of payment is not accepted',
};

const blsErrorCodeToMessagesFr = {
  [BlsErrorCode.BLS_0]: 'Erreur interne',
  [BlsErrorCode.BLS_2]: "Ce mode de paiement n'est pas accepté",
  [BlsErrorCode.BLS_3]: 'Cartes American Express non acceptées',
  [BlsErrorCode.BLS_4]: 'Carte non acceptée',
  [BlsErrorCode.BLS_5]: 'Cartes Visa non acceptées',
  [BlsErrorCode.BLS_6]: "Ce mode de paiement n'est pas accepté",
};

enum ValidationFunction {
  REQUIRED = 'required',
  MIN_LENGTH_1 = 'minLength1',
  MIN_LENGTH_2 = 'minLength2',
  MIN_LENGTH_5 = 'minLength5',
  MIN_LENGTH_6 = 'minLength6',
  MIN_LENGTH_8 = 'minLength8',
  REQUIRED_LENGTH_4 = 'requiredLength4',
  REQUIRED_LENGTH_5 = 'requiredLength5',
  REQUIRED_LENGTH_9 = 'requiredLength9',
  REQUIRED_LENGTH_10 = 'requiredLength10',
  NOT_EMAIL = 'notEmail',
  NOT_PO_BOX = 'notPOBox',
  ALLOWED_REGION_US = 'allowedRegionUS',
  VALID_EMAIL = 'validEmail',
  VALID_REGION_US = 'validRegionUS',
  VALID_REGION_CA = 'validRegionCA',
  VALID_DATE = 'validDate',
  VALID_BIRTHDATE = 'validBirthdate',
  IS_NOT_UNDERAGE_US = 'isNotUnderageUS',
  IS_NOT_UNDERAGE_CA = 'isNotUnderageCA',
  VALID_AMOUNT = 'validAmount',
  MIN_INCOME_US = 'minIncomeUS',
  MAX_INCOME_US = 'maxIncomeUS',
  MIN_INCOME_CA = 'minIncomeCA',
  MAX_INCOME_CA = 'maxIncomeCA',
  VALID_NAME = 'validName',
  IS_VALID_CARD_NUMBER_EN = 'isValidCardNumberEn',
  IS_VALID_CARD_NUMBER_FR = 'isValidCardNumberFr',
  IS_VALID_CARD_EXPIRATION = 'isValidCardExpiration',
  IS_VALID_CARD_CCV = 'isValidCardCCV',
  IS_VALID_CARD_ZIP = 'isValidCardZip',
}

/**
 * Map of validation functions.
 */
const validationFunctions = {
  required: (v: any) => !!v || v === 0,
  minLength1: (v: string) => v.length >= 1,
  minLength2: (v: string) => v.length >= 2,
  minLength5: (v: string) => v.length >= 5,
  minLength6: (v: string) => v.length >= 6,
  minLength8: (v: string) => v.length >= 8,
  requiredLength4: (v: string) => v.length === 4,
  requiredLength5: (v: string) => v.length === 5,
  requiredLength9: (v: string) => v.length === 9,
  requiredLength10: (v: string) => v.length === 10,
  notEmail: (v: string) => v.indexOf('@') === -1,
  notPOBox: (v: string) => !constants.PO_BOX_REGEX.test(v),
  allowedRegionUS: (region: RegionsUS) => true,
  validEmail: (v: any) => v.match(constants.EMAIL_REGEX) !== null,
  validRegionUS: (region: RegionsUS) => !!constants.VALID_REGION_MAP_US[region],
  validRegionCA: (region: RegionsCA) => !!constants.VALID_REGION_MAP_CA[region],
  validDate: (v: string) => v.length === 10 && isValidDate(v),
  validBirthdate: (v: string) => {
    const age = swissknife.getAge(new Date(v)) as number;
    return age <= 125;
  },
  isNotUnderageUS: (v: string, age?: number) => {
    const calculatedAge = swissknife.getAge(new Date(v)) as number;
    return (age ?? calculatedAge) >= 18;
  },
  isNotUnderageCA: (v: string, age?: number) => {
    const calculatedAge = swissknife.getAge(new Date(v)) as number;
    return (age ?? calculatedAge) >= 19;
  },
  validAmount: (v: number) => !!v && 0 < v,
  minIncomeUS: (v: number) => v >= constants.MIN_INCOME_US,
  maxIncomeUS: (v: number) => v <= constants.MAX_INCOME_US,
  minIncomeCA: (v: number) => v >= constants.MIN_INCOME_CA,
  maxIncomeCA: (v: number) => v <= constants.MAX_INCOME_CA,
  validName: (v: string) => v.match(constants.NAME_REGEX) === null,
  isValidCardNumberEn: () => secureForms.isValid('card', 'number'),
  isValidCardNumberFr: () => secureForms.isValid('card', 'number'),
  isValidCardExpiration: () => secureForms.isValid('card', 'expiration'),
  isValidCardCCV: () => secureForms.isValid('card', 'ccv'),
  isValidCardZip: () => secureForms.isValid('card', 'zip'),
};

/**
 * Goes through the validity functions defined for the input in dictionaries and
 * returns an object with an invalid error message that first fails.
 * Returns null when it passes all validations.
 * `validityConfig` contains a list of arrays. Each array holds,
 * validity function name, error message, and an optional value, which if given,
 * removes the input label from the error message.
 * By default, it prepends the input label to the error message.
 *
 * Make sure the validity function name is defined in the `validationFunctions`.
 *
 * @param name: input name
 * @param input: meta state for the input inside formMeta
 */
const validatorPipe = (
  name: string,
  input: InputStateType,
): { name: string; errorMsg: string; input: InputStateType } | null => {
  const validityConfig = input.validityConfig;

  for (const validationCheck of validityConfig) {
    const [validationFnName, error, ignoreLabel] = validationCheck;
    const defaultErrorMsg = ignoreLabel ? error : `${input.label} ${error}`;
    const postProcess = input.postProcess;
    const postProcessFn = postProcess && postProcessFunctions[postProcess];
    const inputValue = input.value;
    const processedInputValue = postProcessFn
      ? postProcessFn(inputValue)
      : inputValue;
    const validationFn =
      validationFunctions[validationFnName as ValidationFunction];
    const isValid = Boolean(validationFn) && validationFn(processedInputValue);
    const isValidatingCardNumber =
      validationFnName === ValidationFunction.IS_VALID_CARD_NUMBER_EN ||
      validationFnName === ValidationFunction.IS_VALID_CARD_NUMBER_FR;

    if (!isValid) {
      let errorMsg;

      if (isValidatingCardNumber) {
        const invalidMsg = secureForms.invalidMsg('number') as BlsErrorCode;

        errorMsg =
          validationFnName === ValidationFunction.IS_VALID_CARD_NUMBER_EN
            ? blsErrorCodeToMessagesEn[invalidMsg]
            : blsErrorCodeToMessagesFr[invalidMsg];
      }

      // Default error message
      if (!errorMsg) {
        errorMsg = defaultErrorMsg;
      }

      return {
        name,
        input,
        errorMsg: errorMsg.replace('[value]', inputValue as string), // Inserting variables to error message
      };
    }
  }

  return null;
};

/**
 * Returns an array of errors for a given map of form inputs.
 * @param pageFormInputs: FormMeta for a given page.
 */
const validate = (pageFormInputs: { [k: string]: any }) => {
  const errors = [];

  for (const [key, input] of Object.entries(pageFormInputs)) {
    if (input && typeof input === 'object' && !input.isHidden) {
      const inputError = validatorPipe(key, input);

      if (inputError) {
        errors.push(inputError);
      }
    }
  }

  return errors;
};

export { validationFunctions, validate };
